import React, { useState } from "react";
import { AnimatePresence } from "framer-motion";
import ContactModal from "../home/hero/ContactModal";
import toast from "react-hot-toast";
import axios from "axios";

const styles = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;1,400&family=Syne:wght@300;400;500;600&display=swap');

.prod-root *{box-sizing:border-box;margin:0;padding:0}
.prod-root{
  --ink:#1E2A3A;
  --green:#4E8A3C;
  --green-light:#7BBF6A;
  --green-pale:#EBF5E7;
  --aqua:#7BBCC8;
  --aqua-pale:#E8F4F7;
  --gold:#C9A96E;
  --gold-pale:#FBF5E8;
  --cream:#F7F6F2;
  --muted:#6B7A8D;
  --border:rgba(30,42,58,0.1);
  --white:#FFFFFF;
  font-family:'Syne',sans-serif;
  color:var(--ink);
  background:var(--white);
  width:100%;
  overflow-x:hidden;
}
.prod-serif{font-family:'Playfair Display',Georgia,serif}

/* PAGE HEADER */
.prod-header{
  background:linear-gradient(160deg, #C8DFC2 0%, #B8D8D8 50%, #A8C8D4 100%);
  padding:5rem max(2.5rem, calc((100vw - 1280px)/2 + 2.5rem)) 4rem;
  position:relative;overflow:hidden;
}
.prod-header::before{
  content:'';position:absolute;inset:0;
  background:
    radial-gradient(ellipse at 80% 20%, rgba(255,255,255,0.4) 0%, transparent 55%),
    radial-gradient(ellipse at 15% 80%, rgba(123,188,200,0.2) 0%, transparent 50%);
  pointer-events:none;
}
.prod-header-inner{position:relative;z-index:1}
.prod-header-eyebrow{font-family:'Syne',sans-serif;font-size:10px;letter-spacing:2.5px;text-transform:uppercase;color:rgba(30,42,58,0.5);margin-bottom:0.75rem}
.prod-header h1{font-family:'Playfair Display',serif;font-size:48px;color:var(--ink);font-weight:400;line-height:1.18;max-width:640px}
.prod-header h1 strong{font-style:italic;color:var(--green)}
.prod-header p{font-family:'Syne',sans-serif;font-size:14px;color:var(--muted);line-height:1.8;max-width:520px;margin-top:1.25rem;font-weight:300}

/* PRODUCTS GRID */
.prod-section{padding:5rem max(2.5rem, calc((100vw - 1280px)/2 + 2.5rem));background:var(--cream)}
.prod-eyebrow{font-family:'Syne',sans-serif;font-size:10px;letter-spacing:2.5px;text-transform:uppercase;color:var(--green);margin-bottom:0.6rem}
.prod-h2{font-family:'Playfair Display',serif;font-size:32px;color:var(--ink);line-height:1.25;font-weight:400;margin-bottom:3rem}
.prod-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:1.5rem}

.prod-card{
  background:white;
  border:1px solid rgba(78,138,60,0.12);
  border-radius:10px;overflow:hidden;
  display:flex;flex-direction:column;
  box-shadow:0 2px 12px rgba(30,42,58,0.05);
  transition:all 0.25s;
}
.prod-card:hover{box-shadow:0 6px 24px rgba(78,138,60,0.12);transform:translateY(-2px)}
.prod-card.featured{border-color:var(--green);box-shadow:0 0 0 1px var(--green),0 2px 12px rgba(78,138,60,0.1)}
.prod-card.featured:hover{box-shadow:0 0 0 1px var(--green),0 8px 28px rgba(78,138,60,0.18);transform:translateY(-2px)}

.prod-band{height:4px}
.prod-band-green{background:linear-gradient(90deg,var(--green),var(--green-light))}
.prod-band-gold{background:linear-gradient(90deg,var(--gold),#D4B87A)}
.prod-band-aqua{background:linear-gradient(90deg,var(--aqua),#A8D8E4)}

.prod-card-top{padding:1.5rem 1.5rem 0;flex:1}
.prod-card-eyebrow{font-family:'Syne',sans-serif;font-size:9px;letter-spacing:2px;text-transform:uppercase;color:var(--muted);margin-bottom:0.6rem}
.prod-card h3{font-family:'Playfair Display',serif;font-size:18px;color:var(--ink);font-weight:400;margin-bottom:0.6rem;line-height:1.3}
.prod-card-desc{font-family:'Syne',sans-serif;font-size:12px;color:var(--muted);line-height:1.7;margin-bottom:1.25rem;font-weight:300}
.prod-tags{display:flex;flex-wrap:wrap;gap:6px;margin-bottom:1.25rem}
.prod-tag{font-family:'Syne',sans-serif;font-size:10px;padding:4px 10px;border-radius:20px;letter-spacing:0.3px}
.prod-tag-green{background:var(--green-pale);color:var(--green)}
.prod-tag-gold{background:var(--gold-pale);color:#8A6820}
.prod-tag-aqua{background:var(--aqua-pale);color:#3A6A7A}

.prod-card-bottom{padding:0 1.5rem 1.5rem}
.prod-divider{height:1px;background:var(--border);margin-bottom:1.25rem}
.prod-btn{width:100%;font-family:'Syne',sans-serif;font-size:11px;font-weight:500;letter-spacing:0.5px;padding:11px;border-radius:6px;border:none;cursor:pointer;transition:background 0.2s}
.prod-btn-green{background:var(--green);color:white}
.prod-btn-green:hover{background:#3D7030}
.prod-btn-outline{background:transparent;color:var(--ink);border:1px solid rgba(78,138,60,0.25)}
.prod-btn-outline:hover{background:var(--green-pale);border-color:var(--green);color:var(--green)}
.prod-pill{display:inline-block;background:var(--green);color:white;font-family:'Syne',sans-serif;font-size:9px;letter-spacing:1.5px;text-transform:uppercase;padding:3px 10px;border-radius:20px;margin-bottom:0.6rem}

/* ADVANCED CARE */
.prod-adv{
  background:linear-gradient(160deg, #3A5C35 0%, #4A6A60 50%, #3A5870 100%);
  margin:0 max(2.5rem, calc((100vw - 1280px)/2 + 2.5rem));
  border-radius:12px;padding:3rem;
  display:grid;grid-template-columns:1fr 1fr;gap:3rem;align-items:center;
  position:relative;overflow:hidden;
}
.prod-adv::before{
  content:'';position:absolute;inset:0;border-radius:12px;
  background:
    radial-gradient(ellipse at 15% 50%, rgba(200,223,194,0.15) 0%, transparent 55%),
    radial-gradient(ellipse at 85% 30%, rgba(123,188,200,0.12) 0%, transparent 50%);
  pointer-events:none;
}
.prod-adv-left{position:relative;z-index:1}
.prod-adv-left .prod-eyebrow{color:var(--green-light)}
.prod-adv-left h2{font-family:'Playfair Display',serif;font-size:26px;color:white;font-weight:400;line-height:1.3;margin-top:0.4rem}
.prod-adv-left p{font-family:'Syne',sans-serif;font-size:13px;color:rgba(255,255,255,0.55);line-height:1.8;margin-top:1rem;font-weight:300}
.prod-adv-right{position:relative;z-index:1;display:flex;flex-direction:column;gap:1rem}
.prod-adv-item{
  display:flex;align-items:flex-start;gap:1rem;padding:1rem;
  background:rgba(255,255,255,0.07);
  border-radius:8px;border:1px solid rgba(255,255,255,0.12);
  transition:background 0.2s;
}
.prod-adv-item:hover{background:rgba(255,255,255,0.12)}
.prod-adv-dot{width:8px;height:8px;border-radius:50%;background:var(--green-light);flex-shrink:0;margin-top:4px}
.prod-adv-item h4{font-family:'Syne',sans-serif;font-size:13px;font-weight:500;color:white;margin-bottom:0.2rem}
.prod-adv-item p{font-family:'Syne',sans-serif;font-size:11px;color:rgba(255,255,255,0.45);line-height:1.6;font-weight:300}

/* EDUCATION VIDEO — single */
.prod-video-sec{padding:5rem max(2.5rem, calc((100vw - 1280px)/2 + 2.5rem));background:var(--cream)}
.prod-row-header{display:flex;align-items:flex-end;justify-content:space-between;margin-bottom:2rem;gap:2rem}
.prod-row-header p{font-family:'Syne',sans-serif;font-size:13px;color:var(--muted);line-height:1.75;max-width:340px;font-weight:300}

.prod-video-wrap{display:grid;grid-template-columns:1fr 1fr;gap:2rem;align-items:center}
.prod-vc{
  border-radius:10px;overflow:hidden;
  border:1px solid rgba(78,138,60,0.12);
  cursor:pointer;transition:all 0.25s;background:white;
  box-shadow:0 2px 12px rgba(30,42,58,0.05);
}
.prod-vc:hover{box-shadow:0 6px 24px rgba(78,138,60,0.12);transform:translateY(-2px)}
.prod-vc-thumb{height:200px;display:flex;align-items:center;justify-content:center;position:relative}
.prod-play{
  width:48px;height:48px;
  background:rgba(255,255,255,0.92);
  border-radius:50%;display:flex;align-items:center;justify-content:center;
  box-shadow:0 4px 16px rgba(0,0,0,0.2);
  transition:transform 0.2s;
}
.prod-vc:hover .prod-play{transform:scale(1.1)}
.prod-play svg{width:14px;height:14px;fill:var(--ink);margin-left:2px}
.prod-vc-body{padding:1.25rem 1.5rem}
.prod-vc-type{font-family:'Syne',sans-serif;font-size:9px;letter-spacing:2px;text-transform:uppercase;color:var(--green);margin-bottom:0.4rem}
.prod-vc-title{font-family:'Playfair Display',serif;font-size:16px;color:var(--ink);line-height:1.4;font-weight:400}
.prod-vc-meta{font-family:'Syne',sans-serif;font-size:11px;color:var(--muted);margin-top:0.4rem;font-weight:300}

.prod-video-text h3{font-family:'Playfair Display',serif;font-size:24px;color:var(--ink);font-weight:400;line-height:1.35;margin-bottom:0.75rem}
.prod-video-text p{font-family:'Syne',sans-serif;font-size:13px;color:var(--muted);line-height:1.8;margin-bottom:1.5rem;font-weight:300}
.prod-vid-banner{
  background:white;border:1px solid rgba(78,138,60,0.15);
  border-left:3px solid var(--green);
  border-radius:8px;padding:1.25rem 1.5rem;
  display:flex;align-items:center;justify-content:space-between;gap:1rem;
}
.prod-vb-text p{font-family:'Syne',sans-serif;font-size:13px;font-weight:500;color:var(--ink)}
.prod-vb-text span{font-family:'Syne',sans-serif;font-size:11px;color:var(--muted);display:block;margin-top:3px;font-weight:300}
.prod-btn-gold{
  background:var(--gold);color:white;font-family:'Syne',sans-serif;
  font-size:11px;font-weight:500;padding:10px 20px;border-radius:6px;
  border:none;cursor:pointer;letter-spacing:0.5px;white-space:nowrap;
  transition:background 0.2s;
}
.prod-btn-gold:hover{background:#B0903A}

/* RESPONSIVE */
@media (max-width: 768px) {
  .prod-header{padding:3.5rem 1.5rem}
  .prod-header h1{font-size:34px}
  .prod-section{padding:3.5rem 1.5rem}
  .prod-h2{font-size:26px;margin-bottom:2rem}
  .prod-grid{grid-template-columns:1fr}
  .prod-adv{grid-template-columns:1fr;gap:2rem;margin:0 1.5rem;padding:2rem}
  .prod-video-sec{padding:3.5rem 1.5rem}
  .prod-video-wrap{grid-template-columns:1fr}
  .prod-row-header{flex-direction:column;align-items:flex-start;gap:1rem}
  .prod-row-header p{max-width:100%}
  .prod-vid-banner{flex-direction:column;align-items:flex-start;gap:1rem;padding:1.25rem}
}
@media (max-width: 480px) {
  .prod-header{padding:2.5rem 1.25rem}
  .prod-header h1{font-size:28px}
  .prod-section{padding:3rem 1.25rem}
  .prod-adv{margin:0 1.25rem;padding:1.75rem}
  .prod-video-sec{padding:3rem 1.25rem}
}
@media (min-width: 1440px) {
  .prod-header h1{font-size:56px}
  .prod-h2{font-size:36px}
  .prod-adv{padding:3.5rem 4rem}
}
`;

const Products = () => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "", facility: "", city: "", state: "", zip: "",
    phone: "", email: "", question: "",
  });

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setFormData({ name: "", facility: "", city: "", state: "", zip: "", phone: "", email: "", question: "" });
  };
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    const { name, email, phone, question, facility, city, state, zip } = formData;
    if (!name || !facility || !city || !state || !zip || !phone || !email || !question) {
      toast.error("Please fill in all required fields."); return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address."); return;
    }
    try {
      await axios.post("https://promedhealth-frontdoor-h4c4bkcxfkduezec.z02.azurefd.net/api/v1/contact-us/", formData);
      toast.success("Your message has been sent. We'll get back to you soon!");
      handleClose();
    } catch {
      toast.error("Failed to send message. Please try again.");
    }
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: styles }} />
      <div className="prod-root">

        {/* PAGE HEADER */}
        <div className="prod-header">
          <div className="prod-header-inner">
            <div className="prod-header-eyebrow">Clinical Solutions</div>
            <h1 className="prod-serif">
              Advanced care,<br /><strong>precisely packaged.</strong>
            </h1>
            <p>ProPack™ bundles, collagen dressings, and custom healing kits — built for the way modern providers work and the way patients deserve to heal.</p>
          </div>
        </div>

        {/* PRODUCTS GRID */}
        <section className="prod-section">
          <div className="prod-eyebrow">Our Products</div>
          <h2 className="prod-h2 prod-serif">Everything you need, bundled right.</h2>
          <div className="prod-grid">

            <div className="prod-card">
              <div className="prod-band prod-band-aqua"></div>
              <div className="prod-card-top">
                <div className="prod-card-eyebrow">Wound Dressings</div>
                <h3 className="prod-serif">Collagen Wound Dressings</h3>
                <p className="prod-card-desc">Pure collagen sheets and powder that support the natural healing cascade across acute and chronic wound presentations.</p>
                <div className="prod-tags">
                  <span className="prod-tag prod-tag-aqua">2×2 Sheet</span>
                  <span className="prod-tag prod-tag-aqua">4×4 Sheet</span>
                  <span className="prod-tag prod-tag-aqua">7×7 Sheet</span>
                  <span className="prod-tag prod-tag-aqua">1g Powder</span>
                </div>
              </div>
              <div className="prod-card-bottom">
                <div className="prod-divider"></div>
                <button className="prod-btn prod-btn-outline" onClick={handleOpen}>Inquire About This Product</button>
              </div>
            </div>

            <div className="prod-card featured">
              <div className="prod-band prod-band-green"></div>
              <div className="prod-card-top">
                <div className="prod-pill">Flagship Bundle</div>
                <div className="prod-card-eyebrow">Clinical Kit</div>
                <h3 className="prod-serif">ProPack™ Bundles</h3>
                <p className="prod-card-desc">All-in-one dispensing kits configured for in-office use. Collagen, wound wash, gloves, and tape—ready when you are.</p>
                <div className="prod-tags">
                  <span className="prod-tag prod-tag-green">Collagen Only</span>
                  <span className="prod-tag prod-tag-green">Clinical Kit</span>
                  <span className="prod-tag prod-tag-green">Complete Care</span>
                </div>
              </div>
              <div className="prod-card-bottom">
                <div className="prod-divider"></div>
                <button className="prod-btn prod-btn-green" onClick={handleOpen}>Inquire About This Product</button>
              </div>
            </div>

            <div className="prod-card">
              <div className="prod-band prod-band-gold"></div>
              <div className="prod-card-top">
                <div className="prod-card-eyebrow">Tailored Configuration</div>
                <h3 className="prod-serif">Customized Healing Packs</h3>
                <p className="prod-card-desc">Patient population, wound type, care setting—we build kits around your clinical protocol, not the other way around.</p>
                <div className="prod-tags">
                  <span className="prod-tag prod-tag-gold">Practice-Built</span>
                  <span className="prod-tag prod-tag-gold">Condition-Specific</span>
                  <span className="prod-tag prod-tag-gold">Volume Pricing</span>
                </div>
              </div>
              <div className="prod-card-bottom">
                <div className="prod-divider"></div>
                <button className="prod-btn prod-btn-outline" onClick={handleOpen}>Request Custom Quote</button>
              </div>
            </div>

          </div>
        </section>

        {/* ADVANCED CARE CALLOUT */}
        <div className="prod-adv">
          <div className="prod-adv-left">
            <div className="prod-eyebrow">Advanced Care Portfolio</div>
            <h2 className="prod-serif">Beyond standard<br />wound care.</h2>
            <p>ProMed's portfolio extends well beyond collagen dressings. Our advanced care line includes next-generation wound management solutions—available through our provider channel, not off the shelf.</p>
          </div>
          <div className="prod-adv-right">
            {[
              { title: "Advanced Biologics & Grafts", desc: "Provider-only access to our most advanced regenerative wound products. Inquiry required." },
              { title: "Specialty Wound Management", desc: "Condition-specific solutions for complex, chronic, and surgical wounds across care settings." },
              { title: "Custom Protocol Development", desc: "Work directly with our clinical team to design a full wound care protocol for your practice." }
            ].map((item, i) => (
              <div key={i} className="prod-adv-item">
                <div className="prod-adv-dot"></div>
                <div>
                  <h4>{item.title}</h4>
                  <p>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* EDUCATION — single video */}
        <section className="prod-video-sec">
          <div className="prod-row-header">
            <div>
              <div className="prod-eyebrow">Education</div>
              <h2 className="prod-h2 prod-serif" style={{ marginBottom: 0 }}>Scan. Heal. Move On.</h2>
            </div>
            {/* <p>Short-form clinical videos for providers and patients — built to drive understanding and the next step.</p> */}
          </div>

          <div className="prod-video-wrap">
            <div className="prod-vc">
              <div className="prod-vc-thumb" style={{ background: "linear-gradient(160deg, #C8DFC2 0%, #7BBCC8 100%)" }}>
                <div className="prod-play">
                  <svg viewBox="0 0 12 12"><path d="M3 2l8 4-8 4V2z" /></svg>
                </div>
              </div>
              <div className="prod-vc-body">
                <div className="prod-vc-type">For Providers</div>
                <div className="prod-vc-title">ProPack™ in your wound care protocol — a clinical walkthrough</div>
                <div className="prod-vc-meta">2 min · Provider education</div>
              </div>
            </div>

            <div className="prod-video-text">
              <h3 className="prod-serif">Clinical education,<br />built for busy practices.</h3>
              <p>Our short-form video library covers everything from ProPack™ clinical walkthroughs and patient home-care guides to HCPCS billing education and the science behind collagen healing.</p>
              <div className="prod-vid-banner">
                <div className="prod-vb-text">
                  <p>New content published weekly</p>
                  <span>Provider walkthroughs · Patient guides · Billing education</span>
                </div>
                <button className="prod-btn-gold">Watch on YouTube</button>
              </div>
            </div>
          </div>
        </section>

      </div>

      <AnimatePresence>
        {open && (
          <ContactModal open={open} handleClose={handleClose}
            formData={formData} handleChange={handleChange} handleSubmit={handleSubmit} />
        )}
      </AnimatePresence>
    </>
  );
};

export default Products;